import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus, Sparkles, TrendingUp, Clock, Users, DollarSign,
  Calendar, AlertCircle, CheckCircle, PauseCircle, Eye, Edit, FileText, Briefcase
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductionManager = () => {
  const [projects, setProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [allQuotes, setAllQuotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [capacityData, setCapacityData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('current');
  const [sourceType, setSourceType] = useState('project'); // 'project' or 'quote'
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiSchedule, setAISchedule] = useState(null);
  const [generatingAI, setGeneratingAI] = useState(false);
  
  const [formData, setFormData] = useState({
    project_id: '',
    project_name: '',
    customer_name: '',
    estimated_hours: 0,
    complexity_score: 1,
    products: '',
    production_notes: '',
    priority: 0
  });

  const statuses = ['Queued', 'In Progress', 'On Hold', 'Under Review', 'Completed'];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const [prodProjects, stats, regularProjects, quotes, capacity] = await Promise.all([
        axios.get(`${API}/production/projects`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/production/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/projects`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/quotes`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/production/capacity-planning`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setProjects(prodProjects.data.projects || []);
      setStats(stats.data);
      setAllProjects(regularProjects.data || []);
      setAllQuotes(quotes.data || []);
      setCapacityData(capacity.data);
    } catch (error) {
      console.error('Failed to load production data:', error);
      toast.error('Failed to load production data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProject = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        ...formData,
        products: formData.products ? formData.products.split(',').map(p => p.trim()) : [],
        status: 'Queued',
        show_on_dashboard: true
      };
      
      await axios.post(`${API}/production/projects`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Project added to production!');
      setShowAddModal(false);
      setFormData({
        project_id: '',
        project_name: '',
        customer_name: '',
        estimated_hours: 0,
        complexity_score: 1,
        products: '',
        production_notes: '',
        priority: 0
      });
      loadData();
    } catch (error) {
      console.error('Failed to add project:', error);
      toast.error('Failed to add project');
    }
  };

  const handleStatusChange = async (projectId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/production/projects/${projectId}/status?new_status=${newStatus}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Status updated to ${newStatus}`);
      loadData();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleGenerateAISchedule = async () => {
    setGeneratingAI(true);
    try {
      const token = localStorage.getItem('token');
      
      // Get all queued and in-progress project IDs
      const projectIds = projects
        .filter(p => p.status === 'Queued' || p.status === 'In Progress')
        .map(p => p.id);
      
      const response = await axios.post(`${API}/production/ai-schedule`, {
        project_ids: projectIds
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAISchedule(response.data);
      setShowAIModal(true);
      toast.success('AI schedule generated!');
    } catch (error) {
      console.error('Failed to generate AI schedule:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate schedule');
    } finally {
      setGeneratingAI(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Queued': 'bg-gray-500',
      'In Progress': 'bg-blue-500',
      'On Hold': 'bg-yellow-500',
      'Under Review': 'bg-purple-500',
      'Completed': 'bg-green-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Queued': Clock,
      'In Progress': TrendingUp,
      'On Hold': PauseCircle,
      'Under Review': Eye,
      'Completed': CheckCircle
    };
    return icons[status] || Clock;
  };

  const getProjectsByStatus = (status) => {
    return projects.filter(p => p.status === status);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight uppercase text-gray-900">
            Production Manager
          </h1>
          <p className="text-gray-700 mt-1 font-medium">
            Kanban workflow and AI-powered scheduling
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleGenerateAISchedule}
            disabled={generatingAI || projects.length === 0}
            className="bg-gradient-to-r from-purple-600 to-blue-600"
          >
            <Sparkles size={16} className="mr-2" />
            {generatingAI ? 'Generating...' : 'AI Schedule'}
          </Button>
          <Button onClick={() => setShowAddModal(true)}>
            <Plus size={16} className="mr-2" />
            Add Project
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-2 border-blue-200 bg-blue-50">
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-blue-600">{stats.total}</p>
              <p className="text-xs text-gray-700 mt-1">Total Projects</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-green-200 bg-green-50">
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-green-600">
                {stats.by_status?.['In Progress'] || 0}
              </p>
              <p className="text-xs text-gray-700 mt-1">In Progress</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-purple-200 bg-purple-50">
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-purple-600">
                {stats.total_hours_estimated?.toFixed(0) || 0}
              </p>
              <p className="text-xs text-gray-700 mt-1">Est. Hours</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-orange-200 bg-orange-50">
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-orange-600">
                {stats.total_hours_remaining?.toFixed(0) || 0}
              </p>
              <p className="text-xs text-gray-700 mt-1">Hrs Remaining</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-yellow-200 bg-yellow-50">
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-yellow-600">
                {stats.avg_complexity || 0}
              </p>
              <p className="text-xs text-gray-700 mt-1">Avg Complexity</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Capacity Planning Panel */}
      {capacityData && (
        <Card className="border-2 border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <TrendingUp className="text-indigo-600" size={20} />
                Capacity Planning & Resource Management
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={selectedMonth === 'current' ? 'default' : 'outline'}
                  onClick={() => setSelectedMonth('current')}
                >
                  Current Month
                </Button>
                <Button
                  size="sm"
                  variant={selectedMonth === 'next' ? 'default' : 'outline'}
                  onClick={() => setSelectedMonth('next')}
                >
                  Next Month
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Capacity Utilization */}
              <Card className={`border-2 ${
                selectedMonth === 'current' 
                  ? capacityData.current_month.utilization > 100 
                    ? 'border-red-300 bg-red-50' 
                    : capacityData.current_month.utilization > 80 
                      ? 'border-yellow-300 bg-yellow-50' 
                      : 'border-green-300 bg-green-50'
                  : capacityData.next_month.utilization > 100 
                    ? 'border-red-300 bg-red-50' 
                    : capacityData.next_month.utilization > 80 
                      ? 'border-yellow-300 bg-yellow-50' 
                      : 'border-green-300 bg-green-50'
              }`}>
                <CardContent className="pt-4">
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-700 uppercase">
                      {selectedMonth === 'current' 
                        ? `${new Date(capacityData.current_month.year, capacityData.current_month.month - 1).toLocaleString('default', { month: 'long' })} ${capacityData.current_month.year}`
                        : `${new Date(capacityData.next_month.year, capacityData.next_month.month - 1).toLocaleString('default', { month: 'long' })} ${capacityData.next_month.year}`
                      }
                    </p>
                    <div className="flex items-baseline gap-2">
                      <p className="text-3xl font-bold text-gray-900">
                        {selectedMonth === 'current' 
                          ? capacityData.current_month.utilization 
                          : capacityData.next_month.utilization}%
                      </p>
                      <p className="text-sm text-gray-600">utilization</p>
                    </div>
                    <div className="text-sm text-gray-700">
                      <p className="font-medium">
                        {selectedMonth === 'current' 
                          ? capacityData.current_month.planned_hours 
                          : capacityData.next_month.planned_hours}h planned / {' '}
                        {selectedMonth === 'current' 
                          ? capacityData.current_month.available_hours 
                          : capacityData.next_month.available_hours}h available
                      </p>
                    </div>
                    <Badge 
                      variant="secondary" 
                      className={
                        (selectedMonth === 'current' ? capacityData.current_month.status : capacityData.next_month.status) === 'overbooked'
                          ? 'bg-red-100 text-red-800'
                          : (selectedMonth === 'current' ? capacityData.current_month.status : capacityData.next_month.status) === 'balanced'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-green-100 text-green-800'
                      }
                    >
                      {selectedMonth === 'current' 
                        ? capacityData.current_month.status.toUpperCase() 
                        : capacityData.next_month.status.toUpperCase()}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Projects Scheduled */}
              <Card className="border-2 border-blue-200 bg-blue-50">
                <CardContent className="pt-4">
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-700 uppercase">Projects Scheduled</p>
                    <p className="text-3xl font-bold text-blue-600">
                      {selectedMonth === 'current' 
                        ? capacityData.current_month.project_count 
                        : capacityData.next_month.project_count}
                    </p>
                    <p className="text-sm text-gray-600">
                      in {selectedMonth === 'current' ? 'current' : 'next'} month
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Active Quotes */}
              <Card className="border-2 border-purple-200 bg-purple-50">
                <CardContent className="pt-4">
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-700 uppercase">Active Quotes</p>
                    <p className="text-3xl font-bold text-purple-600">
                      {capacityData.active_quotes.count}
                    </p>
                    <p className="text-sm text-gray-600">
                      ${capacityData.active_quotes.value.toLocaleString()}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Sales Orders */}
              <Card className="border-2 border-green-200 bg-green-50">
                <CardContent className="pt-4">
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-700 uppercase">Sales Orders</p>
                    <p className="text-3xl font-bold text-green-600">
                      {capacityData.sales_orders.count}
                    </p>
                    <p className="text-sm text-gray-600">
                      ${capacityData.sales_orders.value.toLocaleString()}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Month Comparison */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="border border-gray-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-gray-700">
                    Current Month: {new Date(capacityData.current_month.year, capacityData.current_month.month - 1).toLocaleString('default', { month: 'long' })}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Available Hours:</span>
                      <span className="font-semibold">{capacityData.current_month.available_hours}h</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Planned Hours:</span>
                      <span className="font-semibold">{capacityData.current_month.planned_hours}h</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Utilization:</span>
                      <span className={`font-bold ${
                        capacityData.current_month.utilization > 100 ? 'text-red-600' :
                        capacityData.current_month.utilization > 80 ? 'text-yellow-600' : 'text-green-600'
                      }`}>
                        {capacityData.current_month.utilization}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Projects:</span>
                      <span className="font-semibold">{capacityData.current_month.project_count}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-gray-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-gray-700">
                    Next Month: {new Date(capacityData.next_month.year, capacityData.next_month.month - 1).toLocaleString('default', { month: 'long' })}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Available Hours:</span>
                      <span className="font-semibold">{capacityData.next_month.available_hours}h</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Planned Hours:</span>
                      <span className="font-semibold">{capacityData.next_month.planned_hours}h</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Utilization:</span>
                      <span className={`font-bold ${
                        capacityData.next_month.utilization > 100 ? 'text-red-600' :
                        capacityData.next_month.utilization > 80 ? 'text-yellow-600' : 'text-green-600'
                      }`}>
                        {capacityData.next_month.utilization}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Projects:</span>
                      <span className="font-semibold">{capacityData.next_month.project_count}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Capacity Warning/Recommendation */}
            {((selectedMonth === 'current' && capacityData.current_month.utilization > 100) ||
              (selectedMonth === 'next' && capacityData.next_month.utilization > 100)) && (
              <div className="mt-4 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-red-600 mt-0.5" size={20} />
                  <div>
                    <p className="font-semibold text-red-900">Capacity Overbooked!</p>
                    <p className="text-sm text-red-700 mt-1">
                      Consider moving some projects to the next month to balance workload. 
                      You can adjust project timelines by editing the Target Completion Date.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Kanban Board */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {statuses.map((status) => {
          const statusProjects = getProjectsByStatus(status);
          const StatusIcon = getStatusIcon(status);
          
          return (
            <div key={status} className="space-y-3">
              {/* Column Header */}
              <Card className={`${getStatusColor(status)} text-white`}>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <StatusIcon size={16} />
                      {status}
                    </div>
                    <Badge variant="secondary" className="bg-white/20 text-white">
                      {statusProjects.length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
              </Card>

              {/* Project Cards */}
              <div className="space-y-2">
                {statusProjects.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="py-8 text-center text-gray-400 text-sm">
                      No projects
                    </CardContent>
                  </Card>
                ) : (
                  statusProjects.map((project) => (
                    <Card key={project.id} className="hover:shadow-lg transition-shadow cursor-pointer">
                      <CardContent className="pt-4 space-y-2">
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">
                            {project.project_name}
                          </p>
                          <p className="text-xs text-gray-600">{project.customer_name}</p>
                        </div>
                        
                        <div className="flex items-center gap-1 text-xs text-gray-600">
                          <Clock size={12} />
                          <span>{project.estimated_hours}h est</span>
                        </div>
                        
                        <div className="flex items-center justify-between text-xs">
                          <Badge variant="outline" className="text-xs">
                            Complexity: {project.complexity_score}/5
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            P{project.priority}
                          </Badge>
                        </div>

                        {project.products && project.products.length > 0 && (
                          <p className="text-xs text-gray-600 truncate">
                            📦 {project.products.join(', ')}
                          </p>
                        )}

                        {/* Status Actions */}
                        <div className="flex gap-1 pt-2">
                          {status !== 'Completed' && (
                            <select
                              className="w-full text-xs border rounded px-2 py-1"
                              value={status}
                              onChange={(e) => handleStatusChange(project.id, e.target.value)}
                            >
                              {statuses.map(s => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Project Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Add to Production Queue</DialogTitle>
            <DialogDescription>
              Add confirmed work (projects) or potential work (quotes) to production planning
            </DialogDescription>
          </DialogHeader>
          
          {/* Source Type Tabs */}
          <div className="flex gap-2 border-b">
            <button
              type="button"
              onClick={() => setSourceType('project')}
              className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
                sourceType === 'project'
                  ? 'border-blue-600 text-blue-600 font-semibold'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Briefcase size={16} />
              From Projects (Confirmed)
            </button>
            <button
              type="button"
              onClick={() => setSourceType('quote')}
              className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
                sourceType === 'quote'
                  ? 'border-purple-600 text-purple-600 font-semibold'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <FileText size={16} />
              From Quotes (Potential)
            </button>
          </div>

          <form onSubmit={handleAddProject} className="space-y-4 mt-4">
            {/* Selection Dropdown */}
            <div>
              <Label>
                {sourceType === 'project' ? 'Select Existing Project' : 'Select Active Quote'}
              </Label>
              <Select
                value={formData.project_id}
                onValueChange={(value) => {
                  if (sourceType === 'project') {
                    const project = allProjects.find(p => p.id === value);
                    if (project) {
                      const products = project.milestones?.map(m => m.description).filter(d => d) || [];
                      const estimatedHours = project.milestones?.reduce((sum, m) => sum + (m.hours_estimated || 0), 0) || 0;
                      
                      let complexityScore = 3;
                      const quoteTotal = project.quote_total || 0;
                      if (quoteTotal > 15000 || estimatedHours > 100) {
                        complexityScore = 5;
                      } else if (quoteTotal > 8000 || estimatedHours > 50) {
                        complexityScore = 4;
                      } else if (quoteTotal > 3000 || estimatedHours > 20) {
                        complexityScore = 3;
                      } else if (quoteTotal > 1000 || estimatedHours > 10) {
                        complexityScore = 2;
                      } else {
                        complexityScore = 1;
                      }
                      
                      setFormData({
                        ...formData,
                        project_id: value,
                        project_name: project.project_name || project.client_name,
                        customer_name: project.client_name || project.customer_name,
                        estimated_hours: estimatedHours,
                        complexity_score: complexityScore,
                        products: products.join(', '),
                        priority: project.status === 'Won' ? 1 : 3
                      });
                    }
                  } else {
                    const quote = allQuotes.find(q => q.id === value);
                    if (quote) {
                      const labourHours = quote.labour_hours || 0;
                      const lineItems = quote.line_items || [];
                      const products = lineItems
                        .filter(item => item.category !== 'Labour')
                        .map(item => item.description)
                        .filter(d => d)
                        .slice(0, 3)
                        .join(', ');
                      
                      let complexityScore = 3;
                      const quoteTotal = quote.total || 0;
                      if (quoteTotal > 15000 || labourHours > 100) {
                        complexityScore = 5;
                      } else if (quoteTotal > 8000 || labourHours > 50) {
                        complexityScore = 4;
                      } else if (quoteTotal > 3000 || labourHours > 20) {
                        complexityScore = 3;
                      } else if (quoteTotal > 1000 || labourHours > 10) {
                        complexityScore = 2;
                      } else {
                        complexityScore = 1;
                      }
                      
                      setFormData({
                        ...formData,
                        project_id: value,
                        project_name: `Quote ${quote.quote_number} - ${quote.client_name}`,
                        customer_name: quote.client_name,
                        estimated_hours: labourHours,
                        complexity_score: complexityScore,
                        products: products || 'Various items',
                        priority: quote.status === 'Accepted' ? 1 : 2
                      });
                    }
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={`Select ${sourceType === 'project' ? 'a project' : 'a quote'}...`} />
                </SelectTrigger>
                <SelectContent>
                  {sourceType === 'project' ? (
                    allProjects.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {p.status || 'Project'}
                          </Badge>
                          {p.project_name || p.customer_name}
                        </div>
                      </SelectItem>
                    ))
                  ) : (
                    allQuotes
                      .filter(q => q.status !== 'Rejected')
                      .map(q => (
                        <SelectItem key={q.id} value={q.id}>
                          <div className="flex items-center gap-2">
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${
                                q.status === 'Accepted' ? 'bg-green-50 text-green-700' :
                                q.status === 'Sent' ? 'bg-blue-50 text-blue-700' :
                                'bg-gray-50 text-gray-700'
                              }`}
                            >
                              {q.status}
                            </Badge>
                            {q.quote_number} - {q.client_name} (${q.total?.toLocaleString()})
                          </div>
                        </SelectItem>
                      ))
                  )}
                </SelectContent>
              </Select>
              {sourceType === 'quote' && (
                <p className="text-xs text-amber-600 mt-1">
                  ⚠️ This is potential work. Mark as lower priority if quote is not yet accepted.
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Project Name *</Label>
                <Input
                  value={formData.project_name}
                  onChange={(e) => setFormData({...formData, project_name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Customer Name *</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Estimated Hours *</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={formData.estimated_hours}
                  onChange={(e) => setFormData({...formData, estimated_hours: parseFloat(e.target.value)})}
                  required
                />
              </div>
              <div>
                <Label>Complexity (1-5)</Label>
                <Select
                  value={String(formData.complexity_score)}
                  onValueChange={(value) => setFormData({...formData, complexity_score: parseInt(value)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5].map(n => (
                      <SelectItem key={n} value={String(n)}>
                        {n} - {['Simple','Easy','Medium','Hard','Complex'][n-1]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Input
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value)})}
                />
              </div>
            </div>

            <div>
              <Label>Products/Deliverables (comma-separated)</Label>
              <Input
                value={formData.products}
                onChange={(e) => setFormData({...formData, products: e.target.value})}
                placeholder="e.g., Frame, Base plate, Support beam"
              />
            </div>

            <div>
              <Label>Production Notes</Label>
              <Textarea
                value={formData.production_notes}
                onChange={(e) => setFormData({...formData, production_notes: e.target.value})}
                rows={3}
                placeholder="Special requirements, notes..."
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button type="submit">Add to Production</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* AI Schedule Modal */}
      {aiSchedule && (
        <Dialog open={showAIModal} onOpenChange={setShowAIModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-gray-900 text-xl flex items-center gap-2">
                <Sparkles className="text-purple-600" />
                AI-Generated Production Schedule
              </DialogTitle>
              <DialogDescription>
                Intelligent scheduling analysis and recommendations
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {/* AI Reasoning */}
              <Card className="border-2 border-purple-200 bg-purple-50">
                <CardHeader>
                  <CardTitle className="text-sm text-purple-900">AI Analysis</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-purple-900">{aiSchedule.reasoning}</p>
                </CardContent>
              </Card>

              {/* Schedule */}
              {aiSchedule.schedule && aiSchedule.schedule.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm text-gray-900">Recommended Schedule</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {aiSchedule.schedule.map((item, index) => (
                        <div key={index} className="p-3 bg-gray-50 rounded border">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className="font-semibold text-gray-900">{item.project_name}</p>
                              <p className="text-xs text-gray-600">{item.rationale}</p>
                            </div>
                            <Badge>{index + 1}</Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                            <div>
                              <Calendar size={12} className="inline mr-1" />
                              Start: {item.recommended_start}
                            </div>
                            <div>
                              <Calendar size={12} className="inline mr-1" />
                              End: {item.recommended_end}
                            </div>
                          </div>
                          {item.assigned_staff_count && (
                            <div className="text-xs text-gray-600 mt-1">
                              <Users size={12} className="inline mr-1" />
                              {item.assigned_staff_count} staff recommended
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Warnings */}
              {aiSchedule.warnings && aiSchedule.warnings.length > 0 && (
                <Card className="border-2 border-yellow-200 bg-yellow-50">
                  <CardHeader>
                    <CardTitle className="text-sm text-yellow-900 flex items-center gap-2">
                      <AlertCircle size={16} />
                      Warnings
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="list-disc list-inside text-sm text-yellow-900 space-y-1">
                      {aiSchedule.warnings.map((warning, i) => (
                        <li key={i}>{warning}</li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Suggestions */}
              {aiSchedule.suggestions && aiSchedule.suggestions.length > 0 && (
                <Card className="border-2 border-green-200 bg-green-50">
                  <CardHeader>
                    <CardTitle className="text-sm text-green-900 flex items-center gap-2">
                      <CheckCircle size={16} />
                      Optimization Suggestions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="list-disc list-inside text-sm text-green-900 space-y-1">
                      {aiSchedule.suggestions.map((suggestion, i) => (
                        <li key={i}>{suggestion}</li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Resource Utilization */}
              {aiSchedule.resource_utilization && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm text-gray-900">Resource Utilization</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm text-gray-700">
                      {Object.entries(aiSchedule.resource_utilization).map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                          <span className="font-medium capitalize">{key}:</span>
                          <span>{value}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Timeline */}
              <Card className="border-2 border-blue-200 bg-blue-50">
                <CardContent className="pt-6 text-center">
                  <p className="text-3xl font-bold text-blue-600">
                    {aiSchedule.total_duration_days}
                  </p>
                  <p className="text-sm text-blue-900 mt-1">Days to Complete All Projects</p>
                </CardContent>
              </Card>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAIModal(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ProductionManager;
